import React, { useState } from 'react'
import { Modal, Typography, Checkbox } from 'antd'
import { useTranslation } from 'react-i18next'
import type { BulkUploadRollbackPreview } from '@/types'
import { Button } from '@/components/ui/Button'

const { Text, Paragraph } = Typography

type RollbackConfirmModalProps = {
  open: boolean
  preview: BulkUploadRollbackPreview | null
  loading: boolean
  onConfirm: () => void
  onCancel: () => void
}

export const RollbackConfirmModal: React.FC<RollbackConfirmModalProps> = ({
  open,
  preview,
  loading,
  onConfirm,
  onCancel,
}) => {
  const { t } = useTranslation(['BulkPodUpload', 'Common'])
  const [checked, setChecked] = useState(false)

  React.useEffect(() => {
    if (!open) setChecked(false)
  }, [open])

  return (
    <Modal
      open={open}
      title={t('BulkPodUpload:rollback.title', 'Revert this upload')}
      onCancel={onCancel}
      footer={[
        <Button key="cancel" type="button" variant="outline" onClick={onCancel} disabled={loading}>
          {t('Common:general.cancel', 'Cancel')}
        </Button>,
        <Button
          key="confirm"
          type="button"
          variant="destructive"
          disabled={!checked || loading || !preview}
          onClick={onConfirm}
        >
          {loading
            ? t('BulkPodUpload:rollback.reverting', 'Reverting…')
            : t('BulkPodUpload:rollback.confirmAction', 'Revert this upload')}
        </Button>,
      ]}
      destroyOnClose
    >
      {preview ? (
        <div className="space-y-4">
          <Paragraph type="secondary">
            {t(
              'BulkPodUpload:rollback.description',
              'This will permanently delete database records and S3 files created by this upload. Pre-existing approvals are not modified except for attachments added in this batch.'
            )}
          </Paragraph>
          <ul className="list-disc pl-5 space-y-1 text-sm">
            <li>
              <Text>
                {t('BulkPodUpload:rollback.approvals', 'Approvals to delete')}:{' '}
                <strong>{preview.approvalsToDelete}</strong>
              </Text>
            </li>
            <li>
              <Text>
                {t('BulkPodUpload:rollback.attachments', 'Attachments to delete')}:{' '}
                <strong>{preview.attachmentsToDelete}</strong>
              </Text>
            </li>
            <li>
              <Text>
                {t('BulkPodUpload:rollback.s3', 'S3 objects to delete')}:{' '}
                <strong>{preview.s3ObjectsToDelete}</strong>
              </Text>
            </li>
            <li>
              <Text>
                {t('BulkPodUpload:rollback.queueRows', 'Queue rows affected')}:{' '}
                <strong>{preview.queueRowsAffected}</strong>
              </Text>
            </li>
          </ul>
          <Checkbox checked={checked} onChange={e => setChecked(e.target.checked)}>
            {t(
              'BulkPodUpload:rollback.checkbox',
              'I understand this cannot be undone and want to revert this upload'
            )}
          </Checkbox>
        </div>
      ) : null}
    </Modal>
  )
}

export default RollbackConfirmModal
